import React, { useState, useEffect, useRef } from 'react';
import { 
    UserIcon, 
    BriefcaseIcon, 
    AcademicCapIcon,
    WrenchScrewdriverIcon,
    DocumentTextIcon,
    SwatchIcon,
    EyeIcon,
    PlusIcon,
    TrashIcon,
    ArrowUpIcon,
    ArrowDownIcon,
} from '@heroicons/react/24/outline';
import CvPreview from './CvPreview';
import { useToast } from '../ToastProvider';
import MonthInput from '../MonthInput';

const CvEditor = ({ 
    cvData, 
    cvStyle, 
    template, 
    onDataUpdate, 
    onStyleUpdate, 
    onPreview, 
    onAbTest, 
    onSave,
    onChangeTemplate
}) => {
    const [activeTab, setActiveTab] = useState('personal');
    const { success, error: toastError } = useToast();
    
    // Références pour les sections
    const sectionRefs = useRef({
        summary: null,
        experience: null,
        skills: null,
        education: null,
        projects: null,
        quantification: null // Pour les conseils de quantification
    });

    // Écouter les événements de navigation
    useEffect(() => {
        const handleNavigateToSection = (event) => {
            const { sectionId } = event.detail;
            
            // Mapper les IDs des conseils vers les onglets
            const sectionToTabMap = {
                'summary': 'personal',
                'experience': 'experience',
                'skills': 'skills',
                'education': 'education',
                'projects': 'experience', // Les projets sont dans l'onglet expérience
                'quantification': 'experience', // La quantification concerne les expériences
                'tech-projects': 'experience',
                'marketing-kpis': 'experience',
                'finance-certifications': 'education'
            };
            
            const targetTab = sectionToTabMap[sectionId];
            
            if (targetTab) {
                setActiveTab(targetTab);
                
                // Faire défiler vers la section après un court délai
                setTimeout(() => {
                    const sectionRef = sectionRefs.current[sectionId];
                    if (sectionRef) {
                        sectionRef.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                        });
                    } else {
                        // Fallback: faire défiler vers le haut de l'onglet
                        const tabContent = document.querySelector('.flex-1.overflow-y-auto');
                        if (tabContent) {
                            tabContent.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    }
                }, 300); // Augmenté le délai pour s'assurer que l'onglet est changé
            }
        };

        window.addEventListener('navigateToSection', handleNavigateToSection);
        return () => window.removeEventListener('navigateToSection', handleNavigateToSection);
    }, []);

    const [showPreview, setShowPreview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const handleExpEndDateChange = (id, value) => {
        updateExperience(id, 'endDate', value);
        if (value) {
            updateExperience(id, 'current', false);
        }
    };

    const handleEduEndDateChange = (id, value) => {
        updateEducation(id, 'endDate', value);
        if (value) {
            updateEducation(id, 'current', false);
        }
    };

    const tabs = [
        { id: 'personal', label: 'Informations', icon: UserIcon },
        { id: 'experience', label: 'Expérience', icon: BriefcaseIcon },
        { id: 'education', label: 'Formation', icon: AcademicCapIcon },
        { id: 'skills', label: 'Compétences', icon: WrenchScrewdriverIcon },
        { id: 'style', label: 'Style', icon: SwatchIcon }
    ];

    const handlePersonalInfoChange = (field, value) => {
        onDataUpdate({
            ...cvData,
            personalInfo: {
                ...cvData.personalInfo,
                [field]: value
            }
        });
    };

    const handleSummaryChange = (value) => {
        onDataUpdate({
            ...cvData,
            summary: value
        });
    };

    const addExperience = () => {
        const newExperience = {
            id: Date.now(),
            title: '',
            company: '',
            location: '',
            startDate: '',
            endDate: '',
            current: false,
            description: ''
        };
        onDataUpdate({
            ...cvData,
            experience: [...cvData.experience, newExperience]
        });
    };

    const updateExperience = (id, field, value) => {
        onDataUpdate({
            ...cvData,
            experience: cvData.experience.map(exp => 
                exp.id === id ? { ...exp, [field]: value } : exp
            )
        });
    };

    const removeExperience = (id) => {
        onDataUpdate({
            ...cvData,
            experience: cvData.experience.filter(exp => exp.id !== id)
        });
    };

    const moveExperience = (id, direction) => {
        const index = cvData.experience.findIndex(exp => exp.id === id);
        if (index === -1) return;

        const newExperience = [...cvData.experience];
        if (direction === 'up' && index > 0) {
            [newExperience[index], newExperience[index - 1]] = [newExperience[index - 1], newExperience[index]];
        } else if (direction === 'down' && index < newExperience.length - 1) {
            [newExperience[index], newExperience[index + 1]] = [newExperience[index + 1], newExperience[index]];
        }

        onDataUpdate({
            ...cvData,
            experience: newExperience
        });
    };

    const addEducation = () => {
        const newEducation = {
            id: Date.now(),
            degree: '',
            institution: '',
            location: '',
            startDate: '',
            endDate: '',
            current: false,
            description: ''
        };
        onDataUpdate({
            ...cvData,
            education: [...cvData.education, newEducation]
        });
    };

    const updateEducation = (id, field, value) => {
        onDataUpdate({
            ...cvData,
            education: cvData.education.map(edu => 
                edu.id === id ? { ...edu, [field]: value } : edu
            )
        });
    };

    const removeEducation = (id) => {
        onDataUpdate({
            ...cvData,
            education: cvData.education.filter(edu => edu.id !== id)
        });
    };

    const addSkill = () => {
        const newSkill = {
            id: Date.now(),
            name: '',
            level: 'intermediate'
        };
        onDataUpdate({
            ...cvData,
            skills: [...cvData.skills, newSkill]
        });
    };

    const updateSkill = (id, field, value) => {
        onDataUpdate({
            ...cvData,
            skills: cvData.skills.map(skill => 
                skill.id === id ? { ...skill, [field]: value } : skill
            )
        });
    };

    const removeSkill = (id) => {
        onDataUpdate({
            ...cvData,
            skills: cvData.skills.filter(skill => skill.id !== id)
        });
    };

    const handleStyleChange = (field, value) => {
        onStyleUpdate({
            ...cvStyle,
            [field]: value
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave();
            success('CV enregistré');
        } catch (error) {
            toastError('Erreur lors de la sauvegarde');
        } finally {
            setIsSaving(false);
        }
    };

    const renderPersonalInfo = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations personnelles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                        <input
                            type="text"
                            value={cvData.personalInfo.firstName}
                            onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                        <input
                            type="text"
                            value={cvData.personalInfo.lastName}
                            onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                            type="email"
                            value={cvData.personalInfo.email}
                            onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                        <input
                            type="tel"
                            value={cvData.personalInfo.phone}
                            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                        <input
                            type="text"
                            value={cvData.personalInfo.address}
                            onChange={(e) => handlePersonalInfoChange('address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
                        <input
                            type="url"
                            value={cvData.personalInfo.linkedin}
                            onChange={(e) => handlePersonalInfoChange('linkedin', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Site web</label>
                        <input
                            type="url"
                            value={cvData.personalInfo.website}
                            onChange={(e) => handlePersonalInfoChange('website', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
            </div>

            <div ref={(el) => sectionRefs.current.summary = el}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Résumé professionnel</label>
                <textarea
                    value={cvData.summary}
                    onChange={(e) => handleSummaryChange(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Décrivez brièvement votre profil professionnel..."
                />
            </div>
        </div>
    );

    const renderExperience = () => (
        <div className="space-y-6" ref={(el) => sectionRefs.current.experience = el}>
            {/* Référence pour les conseils de quantification */}
            <div ref={(el) => sectionRefs.current.quantification = el} className="sr-only"></div>
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Expérience professionnelle</h3>
                <button
                    onClick={addExperience}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Ajouter
                </button>
            </div>

            <div className="space-y-4">
                {cvData.experience.map((exp, index) => (
                    <div key={exp.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-800">Expérience {index + 1}</h4>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => moveExperience(exp.id, 'up')}
                                    disabled={index === 0}
                                    className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                >
                                    <ArrowUpIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => moveExperience(exp.id, 'down')}
                                    disabled={index === cvData.experience.length - 1}
                                    className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                >
                                    <ArrowDownIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => removeExperience(exp.id)}
                                    className="p-1 text-red-500 hover:text-red-700"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Poste</label>
                                <input
                                    type="text"
                                    value={exp.title}
                                    onChange={(e) => updateExperience(exp.id, 'title', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise</label>
                                <input
                                    type="text"
                                    value={exp.company}
                                    onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
                                <input
                                    type="text"
                                    value={exp.location}
                                    onChange={(e) => updateExperience(exp.id, 'location', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
                                <div className="flex-1 w-full">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
                                    <MonthInput
                                        value={exp.startDate}
                                        onChange={(v) => updateExperience(exp.id, 'startDate', v)}
                                        className="w-full"
                                    />
                                </div>
                                <div className="flex-1 w-full relative z-10">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
                                    <MonthInput
                                        value={exp.endDate}
                                        onChange={(v) => handleExpEndDateChange(exp.id, v)}
                                        className="w-full"
                                        forceFallback={true}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    checked={exp.current}
                                    onChange={(e) => updateExperience(exp.id, 'current', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">Poste actuel</span>
                            </label>
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea
                                value={exp.description}
                                onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Décrivez vos responsabilités et réalisations..."
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderEducation = () => (
        <div className="space-y-6" ref={(el) => sectionRefs.current.education = el}>
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Formation</h3>
                <button
                    onClick={addEducation}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Ajouter
                </button>
            </div>

            <div className="space-y-4">
                {cvData.education.map((edu, index) => (
                    <div key={edu.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-800">Formation {index + 1}</h4>
                            <button
                                onClick={() => removeEducation(edu.id)}
                                className="p-1 text-red-500 hover:text-red-700"
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Diplôme</label>
                                <input
                                    type="text"
                                    value={edu.degree}
                                    onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Établissement</label>
                                <input
                                    type="text"
                                    value={edu.institution}
                                    onChange={(e) => updateEducation(edu.id, 'institution', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
                                <input
                                    type="text"
                                    value={edu.location}
                                    onChange={(e) => updateEducation(edu.id, 'location', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
                                <div className="flex-1 w-full">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
                                    <MonthInput
                                        value={edu.startDate}
                                        onChange={(v) => updateEducation(edu.id, 'startDate', v)}
                                        className="w-full"
                                    />
                                </div>
                                <div className="flex-1 w-full relative z-10">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
                                    <MonthInput
                                        value={edu.endDate}
                                        onChange={(v) => handleEduEndDateChange(edu.id, v)}
                                        className="w-full"
                                        forceFallback={true}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    checked={edu.current}
                                    onChange={(e) => updateEducation(edu.id, 'current', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">Formation en cours</span>
                            </label>
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea
                                value={edu.description}
                                onChange={(e) => updateEducation(edu.id, 'description', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Décrivez votre formation..."
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderSkills = () => (
        <div className="space-y-6" ref={(el) => sectionRefs.current.skills = el}>
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Compétences</h3>
                <button
                    onClick={addSkill}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Ajouter
                </button>
            </div>

            <div className="space-y-4">
                {cvData.skills.map((skill) => (
                    <div key={skill.id} className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0 bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Compétence</label>
                            <input
                                type="text"
                                value={skill.name}
                                onChange={(e) => updateSkill(skill.id, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
                            <select
                                value={skill.level}
                                onChange={(e) => updateSkill(skill.id, 'level', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="beginner">Débutant</option>
                                <option value="intermediate">Intermédiaire</option>
                                <option value="advanced">Avancé</option>
                                <option value="expert">Expert</option>
                            </select>
                        </div>
                        <button
                            onClick={() => removeSkill(skill.id)}
                            className="p-2 text-red-500 hover:text-red-700 md:mt-6"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderStyle = () => (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Personnalisation du style</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Couleur principale</label>
                    <div className="flex items-center space-x-3">
                        <input
                            type="color"
                            value={cvStyle.primaryColor}
                            onChange={(e) => handleStyleChange('primaryColor', e.target.value)}
                            className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                            type="text"
                            value={cvStyle.primaryColor}
                            onChange={(e) => handleStyleChange('primaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Couleur secondaire</label>
                    <div className="flex items-center space-x-3">
                        <input
                            type="color"
                            value={cvStyle.secondaryColor}
                            onChange={(e) => handleStyleChange('secondaryColor', e.target.value)}
                            className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                            type="text"
                            value={cvStyle.secondaryColor}
                            onChange={(e) => handleStyleChange('secondaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Police</label>
                    <select
                        value={cvStyle.fontFamily}
                        onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="Inter">Inter</option>
                        <option value="Poppins">Poppins</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="Bebas Neue">Bebas Neue</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Taille de police</label>
                    <select
                        value={cvStyle.fontSize}
                        onChange={(e) => handleStyleChange('fontSize', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="small">Petite</option>
                        <option value="medium">Moyenne</option>
                        <option value="large">Grande</option>
                    </select>
                </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Template sélectionné</h4>
                <p className="text-blue-700">{template?.name}</p>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'personal':
                return renderPersonalInfo();
            case 'experience':
                return renderExperience();
            case 'education':
                return renderEducation();
            case 'skills':
                return renderSkills();
            case 'style':
                return renderStyle();
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen overflow-x-hidden min-w-0">
            {/* Sidebar */}
            <div className="w-full md:w-80 bg-gray-50 border-r border-gray-200 flex flex-col min-w-0">
                {/* Tabs */}
                <div className="p-4 border-b border-gray-200">
                    <nav className="space-y-2">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors ${
                                    activeTab === tab.id
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <tab.icon className="w-5 h-5 mr-3" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Actions */}
                <div className="p-4 space-y-3">
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        <EyeIcon className="w-4 h-4 mr-2" />
                        {showPreview ? 'Masquer' : 'Aperçu'}
                    </button>
                    
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 w-full flex flex-col overflow-x-hidden min-w-0">
                {/* Header */}
                <div className="p-4 md:p-6 border-b border-gray-200 bg-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Éditeur de CV</h2>
                            <p className="text-gray-600">Personnalisez votre CV avec le template {template?.name}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                onClick={onChangeTemplate}
                                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                <DocumentTextIcon className="w-4 h-4 mr-2" />
                                Changer de template
                            </button>
                            <button
                                onClick={onPreview}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <EyeIcon className="w-4 h-4 mr-2" />
                                Aperçu complet
                            </button>
                            <button
                                onClick={onAbTest}
                                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                <DocumentTextIcon className="w-4 h-4 mr-2" />
                                A/B Test
                            </button>
                        </div>
                    </div>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {renderTabContent()}
                </div>

                {/* Preview Panel */}
                {showPreview && (
                    <div className="flex-1 overflow-hidden min-w-0">
                        <CvPreview
                            cvData={cvData}
                            cvStyle={cvStyle}
                            template={template}
                            isEmbedded={true}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CvEditor; 